// ActionAvatars.tsx
import React from "react";
import { useTranslation } from "react-i18next";

// Define the simplified interface for a single user (member)
export interface User {
  id: string | number;
  prosecutor_name: string;
  full_name: string;
  user_name: string;
  name: string;
  is_team_leader?: boolean;
}

// Define the props for the ActionAvatars component
interface ActionAvatarsProps {
  title: string;
  users: User[];
  maxAvatars?: number;
  prosecutor: boolean;
}

// Helper function to generate initials (reused from SettingUpTeamModal)
const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts?.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts?.length - 1].charAt(0).toUpperCase()
  );
};

const ActionAvatars: React.FC<ActionAvatarsProps> = ({
  title,
  users,
  maxAvatars = 3,
  prosecutor,
}) => {
  const { t } = useTranslation();
  const displayedUsers = users?.slice(0, maxAvatars);
  const remainingUsersCount = users?.length - displayedUsers?.length;

  return (
    <div className="flex flex-1 items-center justify-between p-3 ">
      <div className="flex items-center">
        <div className="bg-gray-200 p-2 rounded-md mr-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-gray-600"
          >
            <line x1="4" y1="9" x2="20" y2="9"></line>
            <line x1="4" y1="15" x2="20" y2="15"></line>
            <line x1="10" y1="3" x2="8" y2="21"></line>
            <line x1="16" y1="3" x2="14" y2="21"></line>
          </svg>
        </div>
        <div>
          <p className="font-medium text-gray-800">{t(title)}</p>
          <p className="text-sm text-gray-500">
            {users?.length} {t("MemberCount", { count: users?.length || 0 })}
          </p>
        </div>
      </div>
      <div className="flex -space-x-2 overflow-hidden">
        {displayedUsers?.map((user) => (
          <div
            key={user.id}
            className="inline-block h-8 w-8 rounded-full ring-2 ring-white bg-black text-white flex items-center justify-center text-xs font-bold relative"
            title={prosecutor ? user.prosecutor_name : user.full_name}
          >
            {getInitials(
              user.prosecutor_name ||
                user.full_name ||
                user.user_name ||
                user.name
            )}
            {user.is_team_leader && (
              <div className="absolute -top-1 -right-1 bg-yellow-500 rounded-full w-4 h-4 flex items-center justify-center" title={t("TeamLeader")}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
            )}
          </div>
        ))}
        {remainingUsersCount > 0 && (
          <div className="inline-block h-8 w-8 rounded-full ring-2 ring-white bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-700">
            {remainingUsersCount}+
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionAvatars;